import apiClient from '../utils/apiClient';

/**
 * Settings Service
 * Handles all settings-related API calls
 */
const settingsService = {
  /**
   * Get all settings for the current tenant
   */
  getSettings: async () => {
    try {
      const response = await apiClient.get('/settings');
      if (response.data && response.data.general) {
        let storedVal = localStorage.getItem('showPrintRemark');
        if (storedVal === null) {
          storedVal = 'true';
          localStorage.setItem('showPrintRemark', 'true');
        }
        response.data.general.showPrintRemark = storedVal === 'true';
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Update all settings
   * @param {Object} settings - Complete settings object with all categories
   */
  updateSettings: async (settings) => {
    try {
      // Save to localStorage before calling backend, in case backend drops it
      if (settings && settings.general && settings.general.showPrintRemark !== undefined) {
        localStorage.setItem('showPrintRemark', settings.general.showPrintRemark ? 'true' : 'false');
      }
      const response = await apiClient.put('/settings', settings);
      if (response.data && response.data.general) {
        const storedVal = localStorage.getItem('showPrintRemark');
        response.data.general.showPrintRemark = storedVal === 'true';
      }
      return response.data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get specific category settings
   * @param {String} category - Category name (general, product, sales, purchase, business, personal)
   */
  getCategory: async (category) => {
    try {
      const response = await apiClient.get(`/settings/${category}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${category} settings:`, error);
      throw error.response?.data || error;
    }
  },

  /**
   * Update specific category settings
   * @param {String} category - Category name
   * @param {Object} data - Category settings data
   */
  updateCategory: async (category, data) => {
    try {
      const response = await apiClient.put(`/settings/${category}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${category} settings:`, error);
      throw error.response?.data || error;
    }
  },

  /**
   * Reset all settings to default
   */
  resetSettings: async () => {
    try {
      const response = await apiClient.post('/settings/reset');
      return response.data;
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Fetch auto-generated code for a specific entity type
   * @param {String} type - The entity type (customer, supplier, item)
   */
  getAutoCode: async (type) => {
    try {
      const response = await apiClient.get(`/settings/auto-code?type=${type}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching auto code for ${type}:`, error);
      throw error.response?.data || error;
    }
  },

  // ─── Backup / Google Drive ───────────────────────────────────────────────

  /** GET /api/backup/google/status */
  getDriveStatus: async () => {
    try {
      const response = await apiClient.get('/backup/google/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching Drive status:', error);
      throw error.response?.data || error;
    }
  },

  /** Redirects the browser to Google consent screen via backend */
  connectGoogleDrive: () => {
    const token = localStorage.getItem('token');
    const baseUrl = apiClient.defaults.baseURL;
    window.location.href = `${baseUrl}/backup/google/connect?_t=${token}`;
  },

  /** DELETE /api/backup/google/disconnect */
  disconnectGoogleDrive: async () => {
    try {
      const response = await apiClient.delete('/backup/google/disconnect');
      return response.data;
    } catch (error) {
      console.error('Error disconnecting Drive:', error);
      throw error.response?.data || error;
    }
  },

  /** PUT /api/settings/backup — saves schedule, folderId, enabled flag */
  updateBackupSettings: async (backupData) => {
    try {
      const response = await apiClient.put('/settings/backup', backupData);
      return response.data;
    } catch (error) {
      console.error('Error updating backup settings:', error);
      throw error.response?.data || error;
    }
  },

  /** POST /api/backup/google/run — trigger an immediate manual backup */
  triggerManualBackup: async () => {
    try {
      const response = await apiClient.post('/backup/google/run');
      return response.data;
    } catch (error) {
      console.error('Error triggering manual backup:', error);
      throw error.response?.data || error;
    }
  },

  /** GET /api/backup/download — download backup as a file to the user's machine */
  downloadBackup: async (from, to) => {
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to)   params.append('to', to);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await apiClient.get(`/backup/download${query}`, { responseType: 'blob' });
      // derive filename from Content-Disposition header, or fall back to a timestamped name
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match
        ? match[1].replace(/['"]/g, '')
        : `backup_${from ?? ''}_${to ?? ''}_${new Date().toISOString().slice(0,10)}.json`;
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading backup:', error);
      throw error.response?.data || error;
    }
  },
};

export default settingsService;